import React, { useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import ActionSection from "features/terminal-details/common/actionSection";
import { toast } from "react-toastify";
import CarrierFormFields from "./section/carrierFormDetail";
import "./carriersaddEditDetails.scss";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Card } from "ui-components/card/card";
import { getCarrierInfoValidation } from "../../../app/data/carriers/models";
import { useCreateCarrier, useUpdateCarrier } from "hooks/useCarrier";
import UserState from "slices/user/UserState";
import { UserUtils } from "app/data/user/userUtils";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";

interface CarrierInfo {
    carrierID: string;
    carrierName: string;
    status: string;
    phone: string;
    email: string;
    streetAddress: string;
    city: string;
    stateProvince: string;
    zipPostalCode: string;
    country: string;
    scacCode: string;
    motorCarrierNo: string;
    generalLiabilityDate: string;
    autoLiabilityDate: string;
    motorCargoDate?: string;
    workmanCompDate?: string;
    notes?: string;
}

const CarriersAddEditDetails = ({
    onClose,
    initialCarrierData,
    show,
    onTeamCreated
}: {
    onClose: () => void;
    initialCarrierData?: CarrierInfo;
    show: boolean;
    onTeamCreated?: () => void;
}): React.ReactElement => {

    const userState: UserState = useSelector(userSelector);
    const formRef = useRef<FormikProps<CarrierInfo>>(null);

    const initialValues: CarrierInfo = initialCarrierData || {
        carrierID: "(Auto-generated)",
        carrierName: "",
        status: "ACTIVE",
        phone: "",
        email: "",
        streetAddress: "",
        city: "",
        stateProvince: "",
        zipPostalCode: "",
        country: "",
        scacCode: "",
        motorCarrierNo: "",
        generalLiabilityDate: "",
        autoLiabilityDate: "",
        motorCargoDate: "",
        workmanCompDate: "",
        notes: "",
    };

    const { mutateAsync: createCarrier } = useCreateCarrier();
    const { mutateAsync: updateCarrier } = useUpdateCarrier();

    const isEdit = !!initialCarrierData;

    const handleClear = () => {
        formRef.current?.resetForm();
    };

    const handleSubmit = async (values: CarrierInfo) => {
        const payload = {
            carrierName: values.carrierName,
            status: values.status,
            phoneNumber: values.phone,
            carrierEmail: values.email,
            carrierAddress: values.streetAddress,
            city: values.city,
            stateProvince: values.stateProvince,
            zipPostalCode: values.zipPostalCode,
            country: values.country,
            scacCode: values.scacCode,
            mcNumber: values.motorCarrierNo,
            generalLiabilityExpiryDate: values.generalLiabilityDate,
            autoLiabilityExpiryDate: values.autoLiabilityDate,
            motorCargoExpiryDate: values.motorCargoDate,
            workmanCompExpiryDate: values.workmanCompDate,
            notes: values.notes,
        };
        try {
            if (isEdit) {
                await updateCarrier({ id: Number(values.carrierID), payload });
                toast.success("Carrier updated successfully");
            } else {
                await createCarrier(payload);
                toast.success("Carrier added successfully");
            }

            onClose();
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const handleSidebarClose = () => {
        formRef.current?.resetForm(); 
        onClose();                    
    };

    const mainHeaderTitle = isEdit
        ? (initialCarrierData?.carrierName ?? "Edit Carrier")
        : "Add New Carrier";

    return (
        <SlideOutSidebar
            show={show}
            header={mainHeaderTitle}
            onClose={handleSidebarClose}
            size="small"
        >
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validationSchema={getCarrierInfoValidation()}
                innerRef={formRef}
                enableReinitialize={true}
            >
                {(formikProps: FormikProps<CarrierInfo>) => (
                    <Form>
                        {isEdit && (
                            <div className="carrier-view-container">
                                <div className="profile-header-carrier__title">
                                    <span className="profile-header-carrier__title-text">{"Carrier Information"}</span>
                                    {UserUtils.isXGSAdministrator(userState.profile) && (
                                        <div className="actions">
                                            <ActionSection
                                                handleClear={onClose}
                                                handleSave={formikProps.handleSubmit}
                                                spinner={false}
                                                disabled={!formikProps.dirty || !formikProps.isValid}
                                                primaryButtonText="Save Changes"
                                                secondaryButtonText="Cancel"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="carrier-view-container">
                            <CarrierFormFields
                                formikProps={formikProps}
                                isEdit={isEdit}
                                CardComponent={Card}
                            />
                        </div>
                        {!isEdit && (
                            <div className="carrier-add-footer">
                                <ActionSection
                                    handleClear={handleClear}
                                    handleSave={formikProps.handleSubmit}
                                    spinner={false}
                                    disabled={!formikProps.dirty || !formikProps.isValid}
                                    primaryButtonText="Save Carrier"
                                    secondaryButtonText="Cancel"
                                />
                            </div>
                        )}

                    </Form>
                )
                }
            </Formik >
        </SlideOutSidebar >
    );
};

export default CarriersAddEditDetails;